import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import useForm from '@base/Form/useForm';
import Field from '@base/Form/Field';
import Button from '@base/Button/Button';
import Link from '@base/Link/Link';
import DatePicker from '@base/DatePicker/DatePicker';
import NumberField from '@base/NumberField/NumberField';
import Dropdown from '@comp/Dropdown/Dropdown';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import { PREDICT_MY_RANK_CLICKED } from '@constants/segment';
import { useEffect, useState } from 'react';
import deviceHelper from '@utils/deviceHelper';
import reduxFreeFetcher from '@api/fetchers';
import Typography from 'aquilla/core/atoms/Typography';
import { NEET_VALIDATIONS, VALIDATIONS } from '../constant';
import {
  isRequired,
  scoreWithinRange,
  isFutureDate,
  getExamInfo,
  getExamUIDByGoalUID,
  getIsIITJeeRankPredictor,
  isFloat,
  isLengthError,
  AITS_EXAM_UID,
  IIT_JEE_UID,
  isNeetUGApplicationNumber
} from '../utils';
import useRankPredictorSegmentAnalytics from '../Common/useRankPredictorSegmentAnalytics';

const Title = styled(H2)`
  margin-bottom: ${Spacings.SPACING_3B};
`;

const EditorWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
    margin-top: ${({ $isIITJeeRankPredictor }) =>
      $isIITJeeRankPredictor
        ? `${Spacings.SPACING_6B}`
        : `${Spacings.SPACING_10B}`};
  }
`;

const StyledH6 = styled(H6)`
  margin-bottom: ${({ $isIITJeeRankPredictor }) =>
    $isIITJeeRankPredictor
      ? `${Spacings.SPACING_3B}`
      : `${Spacings.SPACING_4B}`};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${({ $isIITJeeRankPredictor }) =>
      $isIITJeeRankPredictor
        ? `${Spacings.SPACING_1B}`
        : `${Spacings.SPACING_4B}`};
  }
`;

const StyledNumberField = styled(NumberField)`
  width: 100%;
`;

export const StyledButton = styled(Button)`
  ${({ $colorType }) =>
    $colorType === 'light_dark' && {
      color: 'var(--color-i-white)',
      backgroundColor: 'var(--color-i-black)',
      marginRight: `${Spacings.SPACING_4B}`
    }}
  &:active {
    background-color: ${({ $colorType }) =>
      $colorType === 'light_dark' && 'var(--color-i-black)'};
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const Error = styled(P2)`
  font-weight: 600;
  color: var(--color-i-red);
  margin-top: ${Spacings.SPACING_2B};
`;

const FieldWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_6B};
`;
const IITExtraFiledsWrapper = styled.div`
  display: flex;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;
const DOEWrapper = styled.div`
  width: 50%;
  ${mediaQuery.uptoMobile} {
    margin-left: 0;
    width: 100%;
  }
`;
const SlotWrapper = styled.div`
  margin-left: ${Spacings.SPACING_6B};
  width: 50%;
  ${mediaQuery.uptoMobile} {
    margin-left: 0;
    width: 100%;
  }
`;
const StyledDropDown = styled(Dropdown)`
  border: 1px solid var(--color-divider);
  background: transparent;
  box-shadow: none;
  width: 100%;
  font-weight: normal;
  padding: ${Spacings.SPACING_4B};
  &:hover {
    box-shadow: none;
  }
  $(mediaQuery.uptoMobile) {
    padding: 0;
  }
  &.dropdown-button {
    padding: ${Spacings.SPACING_4B};
    h6 {
      line-height: initial;
      font-weight: ${Fonts.REGULAR};
    }
  }
`;
const StyledDatePicker = styled(DatePicker)`
  input[value='Select date'] {
    color: var(--color-text-secondary);
  }
  border-radius: ${Spacings.SPACING_2B};
`;

const ApplicationNumberWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
`;

const StyTypography = styled(Typography)`
  width: ${Spacings.SPACING_9B};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${Fonts.EXTRABOLD};
`;

const StyledSpan = styled.div`
  font-size: ${Fonts.P2};
  color: var(--color-text-primary);
  font-weight: ${Fonts.LIGHT};
  margin-bottom: ${Spacings.SPACING_3B};
  a {
    text-decoration: underline;
    color: var(--color-text-primary);
  }
`;

const renderErrorMessage = ({ message }) => {
  return <Error>{message}</Error>;
};

const Form = ({
  initialData,
  onPredict,
  isUnacademyPredictor,
  isOfflineRankPredictor
}) => {
  const { query, isReady } = useRouter();
  let { goalUID } = query;
  if (isOfflineRankPredictor) goalUID = 'TMUVD';
  let { exam_type } = query;
  let examUID = getExamUIDByGoalUID(goalUID);
  if (isUnacademyPredictor) {
    examUID = 'UNACADEMY';
    exam_type = 'UNACADEMY';
  }
  const examInfo = getExamInfo(examUID, exam_type);
  const isIITJeeRankPredictor = getIsIITJeeRankPredictor(examUID);
  const segmentAnalytics = useRankPredictorSegmentAnalytics();
  const [slotsInfo, setSlotsInfo] = useState([]);
  const [DOEList, setDOEList] = useState([]);

  const [filteredSlots, setFilteredSlots] = useState([]);
  const { registrar, isSubmitting, onFormSubmit } = useForm({
    data: initialData
  });
  const fetchSlotsInfo = async () => {
    const { data } = await reduxFreeFetcher(
      `v1/quizzes/exam/rank_predictor/${examUID}/`
    );
    if (data) {
      const dateOfExams = data.reduce((acc, item) => {
        if (acc.indexOf(item.date) === -1) acc.push(item.date);
        return acc;
      }, []);
      setDOEList(dateOfExams);
      setSlotsInfo(data);
    }
  };
  const filterSlots = (selectedDate) => {
    const fSlots = slotsInfo.reduce((acc, item) => {
      if (new Date(item?.date)?.getDate() === selectedDate?.getDate()) {
        acc.push({
          label: item.text,
          value: item.startTime
        });
      }
      return acc;
    }, []);
    setFilteredSlots(fSlots);
    registrar.setData('slot', null);
  };
  useEffect(() => {
    registrar.setData('score', initialData.score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData.score]);
  useEffect(() => {
    if (goalUID === IIT_JEE_UID && isReady && exam_type !== AITS_EXAM_UID) {
      fetchSlotsInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalUID, isReady]);

  const onSubmit = async (data) => {
    const eventsAnalytics = {
      score: data.score
    };
    if (isUnacademyPredictor || isOfflineRankPredictor) {
      eventsAnalytics.is_offline = true;
    }
    segmentAnalytics(PREDICT_MY_RANK_CLICKED, eventsAnalytics);
    await onPredict(data);
  };
  const handleOnSubmit = onFormSubmit(onSubmit);
  return (
    <>
      <Title>{examInfo.heading}</Title>
      <EditorWrapper $isIITJeeRankPredictor={isIITJeeRankPredictor}>
        <StyledH6 $isIITJeeRankPredictor={isIITJeeRankPredictor}>
          Score
          {exam_type === AITS_EXAM_UID && ' in AITS'}
        </StyledH6>
        <FieldWrapper>
          <Field
            registrar={registrar}
            name="score"
            rules={[
              isFloat('Please enter a valid score'),
              isRequired('Score is required'),
              scoreWithinRange(
                'Please enter a valid score',
                examInfo.min,
                examInfo.max
              )
            ]}
            render={({ onChange, value, hasError }) => (
              <StyledNumberField
                allowNegative
                allowFloat
                placeholder={examInfo.scorePlaceholder}
                value={value}
                onChange={(_, data) => onChange(data)}
                $isIITJeeRankPredictor={isIITJeeRankPredictor}
                error={hasError}
              />
            )}
            renderError={renderErrorMessage}
          />
        </FieldWrapper>

        <StyledH6>
          Application number{' '}
          {isIITJeeRankPredictor && 'without first 2 digits (M6)'}
        </StyledH6>
        <FieldWrapper>
          <ApplicationNumberWrapper>
            {isIITJeeRankPredictor && (
              <StyTypography variant="h5">M6-</StyTypography>
            )}
            <Field
              registrar={registrar}
              name="applicationNumber"
              rules={
                isIITJeeRankPredictor
                  ? [isRequired('Application number is required')]
                  : [
                      isRequired('Application number is required'),
                      isLengthError(
                        'Please enter a valid application number',
                        VALIDATIONS.maxLength
                      ),
                      isNeetUGApplicationNumber(
                        'Application number should be of 12 digits starting with 2404',
                        examUID
                      )
                    ]
              }
              render={({ onChange, value, hasError }) => (
                <StyledNumberField
                  placeholder={
                    examInfo.applicationNumberPlaceholder
                      ? examInfo.applicationNumberPlaceholder
                      : `Enter your ${examInfo.name} application number`
                  }
                  value={value}
                  onChange={(_, data) => onChange(data)}
                  error={hasError}
                  inputProps={{
                    maxLength: VALIDATIONS.maxLength
                  }}
                />
              )}
              renderError={renderErrorMessage}
            />
          </ApplicationNumberWrapper>
        </FieldWrapper>
        <StyledH6>Date of birth</StyledH6>
        <FieldWrapper>
          <Field
            registrar={registrar}
            name="dob"
            rules={[
              isRequired('Date of birth is required'),
              isFutureDate('Date of birth cannot be of a future date')
            ]}
            render={({ onChange, value, hasError }) => (
              <StyledDatePicker
                variant="date"
                width="100%"
                value={value}
                onChange={onChange}
                error={hasError}
                minDate={
                  examUID === 'NTAVDLBKV4'
                    ? NEET_VALIDATIONS.minDate
                    : VALIDATIONS.minDate
                }
                maxDate={
                  examUID === 'NTAVDLBKV4'
                    ? NEET_VALIDATIONS.maxDate
                    : VALIDATIONS.maxDate
                }
                hideDateIfEmpty
                placeHolder="Select date"
              />
            )}
            renderError={renderErrorMessage}
          />
        </FieldWrapper>
        {goalUID === IIT_JEE_UID && exam_type !== AITS_EXAM_UID && (
          <IITExtraFiledsWrapper>
            <DOEWrapper>
              <StyledH6>Date of exam</StyledH6>
              <FieldWrapper>
                <Field
                  registrar={registrar}
                  name="doe"
                  rules={[isRequired('Date of exam is required')]}
                  render={({ onChange, value, hasError }) => (
                    <StyledDatePicker
                      variant="date"
                      width="100%"
                      value={value}
                      onChange={(cVal) => {
                        onChange(cVal);
                        filterSlots(cVal);
                      }}
                      error={hasError}
                      minDate={new Date(DOEList[0]).getTime()}
                      maxDate={new Date(DOEList[DOEList.length - 1]).getTime()}
                      hideDateIfEmpty
                      placeHolder="Select date"
                    />
                  )}
                  renderError={renderErrorMessage}
                />
              </FieldWrapper>
            </DOEWrapper>
            <SlotWrapper className="shift">
              <StyledH6>Slot</StyledH6>
              <FieldWrapper>
                <Field
                  registrar={registrar}
                  name="slot"
                  rules={[isRequired('Slot is required')]}
                  render={({ onChange, value }) => (
                    <StyledDropDown
                      className="dropdown-button"
                      options={filteredSlots}
                      onOptionClick={onChange}
                      selectedOption={value}
                      menuWidth={
                        deviceHelper.isMobileDevice() ? '100%' : '220px'
                      }
                      placeholder="Choose slot"
                    />
                  )}
                  renderError={renderErrorMessage}
                />
              </FieldWrapper>
            </SlotWrapper>
          </IITExtraFiledsWrapper>
        )}
      </EditorWrapper>
      <StyledSpan>
        By continuing, you agree to our{' '}
        <Link href="/rank-predictor-terms">Terms & Conditions</Link>
      </StyledSpan>
      <StyledButton
        label="Predict my rank"
        variant={isSubmitting ? 'disabled' : 'filled'}
        onClick={handleOnSubmit}
        $colorType={examInfo.buttonColor}
      />
    </>
  );
};

export default Form;
